import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Download, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { goapQueries, planQueries } from '@/db/queries';
import { PlanWorkflow } from '@/lib/plan-workflow';
import { toast } from 'sonner';
import PageTransition from '@/components/PageTransition';
import type { Database } from '@/integrations/supabase/types';

type GOAPExecution = Database['public']['Tables']['goap_executions']['Row'];

/**
 * PlanGeneration Page - Phase 4 Progress Monitoring
 *
 * Real-time progress tracking for the complete emergency plan workflow:
 * 1. Validate interview data
 * 2. Execute research agents (parallel)
 * 3. Compile plan data
 * 4. Generate PDF
 * 5. Deliver plan
 *
 * Features:
 * - Real-time progress updates (polls every 2 seconds)
 * - Agent-by-agent status display
 * - Current action indicator
 * - Error handling with retry
 * - PDF download on completion
 * - Auto-navigation to dashboard
 *
 * GOAP State Machine:
 * - Initial State: { interview_complete: true }
 * - Goal State: { pdf_delivered: true }
 * - Actions: validate → research → compile → generate → deliver
 */
const PlanGeneration = () => {
  const { interviewId } = useParams<{ interviewId: string }>();
  const navigate = useNavigate();

  // Workflow state
  const [goapExecution, setGoapExecution] = useState<GOAPExecution | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  // Polling control
  const [isPolling, setIsPolling] = useState(true);

  /**
   * Initialize workflow execution
   * Creates PlanWorkflow instance and starts the end-to-end process
   */
  useEffect(() => {
    const startWorkflow = async () => {
      if (!interviewId) {
        setError('No interview ID provided');
        setIsLoading(false);
        return;
      }

      try {
        console.log('🚀 Starting plan generation workflow for interview:', interviewId);

        // Check if workflow already exists
        const existingExecution = await goapQueries.getByInterviewId(interviewId);

        if (existingExecution) {
          console.log('✅ Found existing workflow execution:', existingExecution.id);
          setGoapExecution(existingExecution);

          // Check if already completed
          if (existingExecution.status === 'completed') {
            console.log('✅ Workflow already completed');

            // Fetch PDF URL from emergency_plans table
            const plan = await planQueries.getLatest(interviewId);
            if (plan?.pdf_url) {
              setPdfUrl(plan.pdf_url);
            }

            setIsPolling(false);
            setIsLoading(false);
            return;
          }

          // If failed, allow retry
          if (existingExecution.status === 'failed') {
            console.log('⚠️ Previous workflow failed, ready for retry');
            setIsLoading(false);
            return;
          }
        } else {
          // Start new workflow execution
          console.log('🆕 Starting new workflow execution');

          const workflow = new PlanWorkflow(interviewId);

          // Execute workflow asynchronously (don't await here - let it run in background)
          workflow.execute().then(result => {
            console.log('✅ Workflow execution complete:', result);

            if (result.success && result.pdfUrl) {
              setPdfUrl(result.pdfUrl);
              toast.success('Your emergency plan is ready!');

              // Auto-navigate to dashboard after 3 seconds
              setTimeout(() => {
                navigate('/dashboard');
              }, 3000);
            } else {
              console.error('❌ Workflow failed:', result.errors);
              toast.error('Plan generation failed. Please try again.');
            }
          }).catch(error => {
            console.error('❌ Workflow execution error:', error);
            toast.error('An error occurred during plan generation');
          });
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error('❌ Failed to start workflow:', err);
        setError(err.message || 'Failed to start plan generation');
        setIsLoading(false);
      }
    };

    startWorkflow();
  }, [interviewId, navigate]);

  /**
   * Poll GOAP execution status every 2 seconds
   * Updates UI with real-time progress from database
   */
  useEffect(() => {
    if (!interviewId || !isPolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const execution = await goapQueries.getByInterviewId(interviewId);

        if (execution) {
          setGoapExecution(execution);

          // Stop polling if completed or failed
          if (execution.status === 'completed' || execution.status === 'failed') {
            setIsPolling(false);

            // Fetch PDF URL on completion
            if (execution.status === 'completed') {
              const plan = await planQueries.getLatest(interviewId);
              if (plan?.pdf_url) {
                setPdfUrl(plan.pdf_url);
                toast.success('Your emergency plan is ready!');

                // Auto-navigate to dashboard after 3 seconds
                setTimeout(() => {
                  navigate('/dashboard');
                }, 3000);
              }
            }
          }
        }
      } catch (err) {
        console.error('❌ Failed to poll workflow status:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [interviewId, isPolling, navigate]);

  /**
   * Retry failed workflow
   * Creates new PlanWorkflow instance and restarts execution
   */
  const handleRetry = async () => {
    if (!interviewId) return;

    setError(null);
    setIsLoading(true);
    setIsPolling(true);

    try {
      const workflow = new PlanWorkflow(interviewId);
      const result = await workflow.execute();

      if (result.success && result.pdfUrl) {
        setPdfUrl(result.pdfUrl);
        toast.success('Your emergency plan is ready!');

        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        setError(result.errors.join(', '));
        toast.error('Plan generation failed. Please try again.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate plan');
      toast.error('An error occurred during plan generation');
    } finally {
      setIsLoading(false);
      setIsPolling(false);
    }
  };

  /**
   * Download PDF from Supabase storage
   */
  const handleDownloadPDF = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
      toast.success('Opening your emergency plan...');
    }
  };

  /**
   * Get status icon for current workflow state
   */
  const getStatusIcon = () => {
    if (!goapExecution) {
      return <Loader2 className="h-8 w-8 animate-spin text-primary" />;
    }

    switch (goapExecution.status) {
      case 'completed':
        return <CheckCircle2 className="h-8 w-8 text-green-500" />;
      case 'failed':
        return <XCircle className="h-8 w-8 text-red-500" />;
      case 'running':
      default:
        return <Loader2 className="h-8 w-8 animate-spin text-primary" />;
    }
  };

  /**
   * Get human-readable status message
   */
  const getStatusMessage = () => {
    if (!goapExecution) {
      return 'Initializing plan generation...';
    }

    if (goapExecution.status === 'completed') {
      return 'Your emergency plan is ready!';
    }

    if (goapExecution.status === 'failed') {
      return 'Plan generation failed';
    }

    // Show current action if available
    if (goapExecution.current_action) {
      const actionMessages: Record<string, string> = {
        validate_interview: 'Validating your interview responses...',
        execute_research: 'Researching emergency information for your location...',
        compile_plan: 'Compiling your personalized emergency plan...',
        generate_pdf: 'Creating your professional PDF document...',
        deliver_plan: 'Finalizing and saving your plan...',
      };

      return actionMessages[goapExecution.current_action] || 'Processing...';
    }

    return 'Generating your emergency plan...';
  };

  // Loading state
  if (isLoading && !goapExecution) {
    return (
      <PageTransition>
        <div className="container mx-auto max-w-2xl py-8 px-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin" />
                Starting Plan Generation
              </CardTitle>
              <CardDescription>
                Initializing workflow...
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </PageTransition>
    );
  }

  // Error state
  if (error && !goapExecution) {
    return (
      <PageTransition>
        <div className="container mx-auto max-w-2xl py-8 px-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-500">
                <XCircle className="h-6 w-6" />
                Error Starting Workflow
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/chat')} variant="outline">
                Return to Interview
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="container mx-auto max-w-2xl py-8 px-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon()}
                <div>
                  <CardTitle>Emergency Plan Generation</CardTitle>
                  <CardDescription>{getStatusMessage()}</CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Overall Progress</span>
                <span className="font-semibold text-primary">
                  {goapExecution?.progress_percentage || 0}%
                </span>
              </div>
              <Progress
                value={goapExecution?.progress_percentage || 0}
                className="h-3"
              />
            </div>

            <Separator />

            {/* Action Plan Steps */}
            {goapExecution?.action_plan && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground">
                  Workflow Steps
                </h3>
                <div className="space-y-2">
                  {(goapExecution.action_plan as any[]).map((action: any, index: number) => {
                    const isCompleted = (goapExecution.completed_actions || []).includes(action.name);
                    const isCurrent = goapExecution.current_action === action.name;

                    return (
                      <div
                        key={action.name}
                        className={`flex items-center gap-3 p-3 rounded-lg border ${
                          isCompleted
                            ? 'bg-green-50 border-green-200'
                            : isCurrent
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-muted/50 border-border'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : isCurrent ? (
                          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/50" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{action.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Warnings */}
            {goapExecution?.last_error && goapExecution.status !== 'completed' && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {goapExecution.last_error}
                </AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              {/* Retry button for failed workflows */}
              {goapExecution?.status === 'failed' && (
                <Button onClick={handleRetry} variant="default" className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Generation
                </Button>
              )}

              {/* Download button when completed */}
              {goapExecution?.status === 'completed' && pdfUrl && (
                <Button onClick={handleDownloadPDF} variant="default" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download Your Plan
                </Button>
              )}

              {/* Return to dashboard */}
              {goapExecution?.status === 'completed' && (
                <Button onClick={() => navigate('/dashboard')} variant="outline">
                  Go to Dashboard
                </Button>
              )}
            </div>

            {/* Completion message */}
            {goapExecution?.status === 'completed' && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Your personalized emergency plan has been generated successfully!
                  You'll be redirected to your dashboard in a few seconds.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  );
};

export default PlanGeneration;
